import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { fetchInteractions, fetchInteractionTypes, updateInteractionStatus as apiUpdateInteractionStatus, updateInteractionDetails as apiUpdateInteractionDetails, updateInteractionUnreadStatus as apiUpdateInteractionUnreadStatus } from '../utils/api';
import { initializeAuth } from '../utils/auth';

// Initial state - now loads from API
const initialState = {
  interactions: [],
  interactionTypes: [],
  currentInteraction: null,
  widgetState: {
    isOpen: false,
    position: { x: window.innerWidth - 100, y: window.innerHeight - 100 },
    view: 'callQueue', // 'callQueue' or 'conversation'
  },
  transcriptState: {
    isOpen: false,
  },
  pmsView: 'scheduler', // 'scheduler' or 'addPatient' or 'addAppointment'
  pmsUrl: 'mock', // Default PMS URL - set to 'mock' for Phase 1 development
  appointments: [
    { id: 1, provider: 'Scott, Michael', top: 1, height: 4, color: 'bg-orange-200 border-orange-400', title: 'Pedo Treatment' },
    { id: 2, provider: 'Scott, Michael', top: 8, height: 4, color: 'bg-pink-200 border-pink-400', title: 'Restorative' },
  ],
  bookingStep: 'idle', // idle, fillPatient, confirmPatient, fillAppointment, confirmAppointment, saving, success
  
  // Access token state
  accessToken: null,
  accessTokenValidation: {
    isValidated: false,
    validatedAt: null,
    environment: null,
    bypassed: false,
  },
};

// Action types
export const ACTIONS = {
  // Authentication actions
  SET_AUTH_STATE: 'SET_AUTH_STATE',
  SET_AUTH_ERROR: 'SET_AUTH_ERROR',
  
  // Access token actions
  SET_ACCESS_TOKEN: 'SET_ACCESS_TOKEN',
  SET_ACCESS_TOKEN_VALIDATION: 'SET_ACCESS_TOKEN_VALIDATION',
  
  // Data loading actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_INTERACTIONS: 'SET_INTERACTIONS',
  SET_INTERACTION_TYPES: 'SET_INTERACTION_TYPES',
  
  // New action for optimistic update rollbacks
  SET_INTERACTION: 'SET_INTERACTION',

  // Existing actions
  SELECT_INTERACTION: 'SELECT_INTERACTION',
  UPDATE_INTERACTION_STATUS: 'UPDATE_INTERACTION_STATUS',
  UPDATE_INTERACTION_UNREAD_STATUS: 'UPDATE_INTERACTION_UNREAD_STATUS',
  ADD_INTERACTION: 'ADD_INTERACTION',
  TOGGLE_WIDGET: 'TOGGLE_WIDGET',
  UPDATE_WIDGET_POSITION: 'UPDATE_WIDGET_POSITION',
  CHANGE_WIDGET_VIEW: 'CHANGE_WIDGET_VIEW',
  SET_PMS_URL: 'SET_PMS_URL',
  UPDATE_INTERACTION_DETAILS: 'UPDATE_INTERACTION_DETAILS',
  SET_PMS_VIEW: 'SET_PMS_VIEW',
  ADD_APPOINTMENT: 'ADD_APPOINTMENT',
  TOGGLE_TRANSCRIPT: 'TOGGLE_TRANSCRIPT',
  SET_BOOKING_STEP: 'SET_BOOKING_STEP',
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_AUTH_STATE:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        authError: null,
      };

    case ACTIONS.SET_AUTH_ERROR:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        authError: action.payload,
        isLoading: false,
      };

    case ACTIONS.SET_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.payload,
      };

    case ACTIONS.SET_ACCESS_TOKEN_VALIDATION:
      return {
        ...state,
        accessTokenValidation: {
          ...state.accessTokenValidation,
          ...action.payload,
        },
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case ACTIONS.SET_INTERACTIONS:
      return {
        ...state,
        interactions: action.payload,
        isLoading: false,
        error: null,
      };

    case ACTIONS.SET_INTERACTION_TYPES:
      return {
        ...state,
        interactionTypes: action.payload,
      };

    case ACTIONS.SET_INTERACTION:
      return {
        ...state,
        interactions: state.interactions.map(interaction =>
          interaction.id === action.payload.id ? action.payload : interaction
        ),
        currentInteraction: state.currentInteraction?.id === action.payload.id ? action.payload : state.currentInteraction,
      };

    case ACTIONS.SELECT_INTERACTION:
      return {
        ...state,
        currentInteraction: action.payload,
        bookingStep: 'idle',
        widgetState: {
          ...state.widgetState,
          view: action.payload ? 'conversation' : state.widgetState.view,
        },
      };

    case ACTIONS.UPDATE_INTERACTION_STATUS:
      return {
        ...state,
        interactions: state.interactions.map(interaction =>
          interaction.id === action.payload.interactionId
            ? { ...interaction, status: action.payload.status }
            : interaction
        ),
        currentInteraction: state.currentInteraction && state.currentInteraction.id === action.payload.interactionId
          ? { ...state.currentInteraction, status: action.payload.status }
          : state.currentInteraction,
      };

    case ACTIONS.UPDATE_INTERACTION_UNREAD_STATUS:
      return {
        ...state,
        interactions: state.interactions.map(interaction =>
          interaction.id === action.payload.interactionId
            ? { ...interaction, unread: action.payload.status }
            : interaction
        ),
        currentInteraction: state.currentInteraction && state.currentInteraction.id === action.payload.interactionId
          ? { ...state.currentInteraction, unread: action.payload.status }
          : state.currentInteraction,
      };

    case ACTIONS.ADD_INTERACTION:
      return {
        ...state,
        interactions: [action.payload, ...state.interactions],
      };

    case ACTIONS.TOGGLE_WIDGET:
      return {
        ...state,
        widgetState: {
          ...state.widgetState,
          isOpen: !state.widgetState.isOpen,
        },
      };

    case ACTIONS.UPDATE_WIDGET_POSITION:
      return {
        ...state,
        widgetState: {
          ...state.widgetState,
          position: action.payload,
        },
      };

    case ACTIONS.CHANGE_WIDGET_VIEW:
      return {
        ...state,
        widgetState: {
          ...state.widgetState,
          view: action.payload,
        },
      };

    case ACTIONS.SET_PMS_URL:
      return {
        ...state,
        pmsUrl: action.payload,
      };

    case ACTIONS.UPDATE_INTERACTION_DETAILS:
      const { interactionId, details, topLevelFields } = action.payload;
      return {
        ...state,
        interactions: state.interactions.map(interaction => {
          if (interaction.id === interactionId) {
            const updatedInteraction = { ...interaction };
            if (details) {
              updatedInteraction.details = { ...interaction.details, ...details };
            }
            if (topLevelFields) {
              Object.assign(updatedInteraction, topLevelFields);
            }
            return updatedInteraction;
          }
          return interaction;
        }),
        currentInteraction: state.currentInteraction && state.currentInteraction.id === interactionId
          ? { 
              ...state.currentInteraction, 
              details: details ? { ...state.currentInteraction.details, ...details } : state.currentInteraction.details,
              ...topLevelFields 
            }
          : state.currentInteraction,
      };

    case ACTIONS.SET_PMS_VIEW:
      return {
        ...state,
        pmsView: action.payload,
      };

    case ACTIONS.ADD_APPOINTMENT:
      return {
        ...state,
        appointments: [...state.appointments, { id: state.appointments.length + 2, ...action.payload }],
      };

    case ACTIONS.TOGGLE_TRANSCRIPT:
      return {
        ...state,
        transcriptState: {
          ...state.transcriptState,
          isOpen: !state.transcriptState.isOpen,
        },
      };

    case ACTIONS.SET_BOOKING_STEP:
      return {
        ...state,
        bookingStep: action.payload,
      };

    default:
      return state;
  }
}

// Create context
const AppContext = createContext();

// Provider component with API integration
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load initial data from API with authentication
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        
        // Initialize access token first
        const accessToken = window.VALIDATED_ACCESS_TOKEN;
        const accessTokenValidated = window.ACCESS_TOKEN_VALIDATED === 'true';
        const validationInfo = window.ACCESS_TOKEN_VALIDATION_INFO || {};
        
        if (accessToken) {
          console.log('🔑 Setting access token in context:', accessToken.substring(0, 8) + '...');
          dispatch({ 
            type: ACTIONS.SET_ACCESS_TOKEN, 
            payload: accessToken 
          });
          
          dispatch({ 
            type: ACTIONS.SET_ACCESS_TOKEN_VALIDATION, 
            payload: {
              isValidated: accessTokenValidated,
              validatedAt: new Date().toISOString(),
              environment: validationInfo.environment || 'unknown',
              bypassed: validationInfo.bypassed || false,
            }
          });
        }
        
        // Initialize authentication
        console.log('Initializing authentication...');
        const authResult = await initializeAuth();
        
        if (authResult.success) {
          console.log('Authentication successful:', authResult.user);
          dispatch({ 
            type: ACTIONS.SET_AUTH_STATE, 
            payload: { 
              isAuthenticated: true, 
              user: authResult.user 
            } 
          });
          
          // Load interactions and interaction types in parallel after authentication
          console.log('Loading data from API...');
          const [interactionsData, interactionTypesData] = await Promise.all([
            fetchInteractions(),
            fetchInteractionTypes()
          ]);

          // Update state with loaded data
          if (interactionsData && interactionsData.interactions) {
            dispatch({ type: ACTIONS.SET_INTERACTIONS, payload: interactionsData.interactions });
          }
          
          if (interactionTypesData && interactionTypesData.interactionTypes) {
            dispatch({ type: ACTIONS.SET_INTERACTION_TYPES, payload: interactionTypesData.interactionTypes });
          }
        } else {
          console.error('Authentication failed:', authResult.error);
          dispatch({ 
            type: ACTIONS.SET_AUTH_ERROR, 
            payload: authResult.error 
          });
        }

      } catch (error) {
        console.error('Failed to load initial data:', error);
        dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      }
    };

    loadInitialData();
  }, []);

  // Enhanced dispatch that handles API calls
  const enhancedDispatch = async (action) => {
    try {
      // Handle API-backed actions
      switch (action.type) {
        case ACTIONS.UPDATE_INTERACTION_STATUS:
          // This could also be made optimistic, but for now it's pessimistic
          try {
            const { interactionId, status } = action.payload;
            await apiUpdateInteractionStatus(interactionId, status);
            dispatch(action);
          } catch (error) {
            console.error('Failed to update interaction status:', error);
          }
          break;

        case ACTIONS.UPDATE_INTERACTION_UNREAD_STATUS:
          try {
            const { interactionId, status } = action.payload;
            await apiUpdateInteractionUnreadStatus(interactionId, status);
            dispatch(action);
          } catch (error) {
            console.error('Failed to update interaction unread status:', error);
          }
          break;

        case ACTIONS.UPDATE_INTERACTION_DETAILS: {
          const { interactionId, details, topLevelFields } = action.payload;
          
          const originalInteraction = state.interactions.find(i => i.id === interactionId);
          if (!originalInteraction) {
            console.error("Optimistic update failed: could not find original interaction to snapshot.");
            return;
          }

          // 1. Optimistic UI update
          dispatch(action);

          // 2. Perform API call in the background
          try {
            await apiUpdateInteractionDetails(interactionId, details, topLevelFields);
          } catch (error) {
            console.error('Failed to update interaction details, rolling back state:', error);
            // 3. On failure, roll back to the original state
            dispatch({ type: ACTIONS.SET_INTERACTION, payload: originalInteraction });
            // Optionally dispatch a global error to notify the user
            dispatch({ type: ACTIONS.SET_ERROR, payload: "Failed to save changes." });
          }
          break;
        }

        default:
          dispatch(action); // Normal dispatch for non-API actions
          break;
      }
    } catch (error) {
      console.error('Dispatch error:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  return (
    <AppContext.Provider value={{ state, dispatch: enhancedDispatch }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
} 